"use client"

import { Helmet } from "react-helmet-async"
import {
    CheckCircle,
    GraduationCap,
} from "lucide-react"
import { Button, Card, CardContent } from "@mui/material"
import { Link } from "react-router-dom";

const courses = [
  {
    id: 4,
    title: "Foundation Course (Class 8)",
    subtitle: "Build Basics. Build Confidence.",
    description:
      "Lay the groundwork with strong fundamentals in Science, Math, English, and Social Science for a lifetime of academic success.",
    points: [
      "✨ Conceptual clarity",
      "🧩 Interactive Learning",
      "🧾 Regular assessments",
      "🌱 Confidence Building:",
      "🧠 Skill Development:",
    ],
    image: "../images/thumbnails/batch thumbnail class 8.png",
    category: "Foundation",
    duration: "Academic Year",
    batchSize: "Small Batch",
    link: "/courses/class-8-foundation",
  },
  {
    id: 5,
    title: "Foundation Course (Class 9)",
    subtitle: "Strengthen Concepts. Boost Confidence.",
    description:
      "Structured preparation for Class 9 students to connect learning with practical application through weekly assessments and tests.",
    points: [
      "📊 Strong Foundation",
      "🧮 Analytical Approach",
      "🧑‍🏫 Personal Mentorship",
      "⏰ Weekly Tests",
      "🚀 Exam Readiness",
    ],
    image: "../images/thumbnails/batch thumbnail class 9.png",

    category: "Foundation",
    duration: "Academic Year",
    batchSize: "Small Batch",
    link: "/courses/class-9-foundation",
  },
  {
    id: 6,
    title: "Board Prep (Class 10)",
    subtitle: "Prepare. Practice. Perform.",
    description:
      "Comprehensive board preparation with chapter-wise tests, full syllabus mocks, and revision cycles for top exam results.",
    points: [
      "🧩 Chapter-Wise Mastery",
      "📚 Full Syllabus Mocks",
      "🧠 Smart Revision Plans",
      "💬 Doubt Clinics",
      "🏆 Performance Analysis",
    ],
    image: "../images/thumbnails/batch thumbnail class 10.png",

    category: "Competitive",
    duration: "3-4 months",
    batchSize: "Small Batch",
    link: "/courses/class-10-board-preparation",
  },
  {
    id: 7,
    title: "Foundation Course (Class 11)",
    subtitle: "The Leap to Higher Learning.",
    description:
      "Focused guidance for transition to senior school with conceptual clarity in Physics, Chemistry, Maths, and Biology.",
    points: [
      "⚛️ Core Subject Focus",
      "🧮 Bridge Learning",
      "📖 Structured Study Plan",
      "🧑‍🏫 Mentor-Led Sessions",
      "🎯 Career Foundation",
    ],
    image: "../images/thumbnails/batch thumbnail class 11.png",

    category: "Competitive",
    duration: "3-4 months",
    batchSize: "Small Batch",
    link: "/courses/class-11-foundation",
  },
  {
    id: 8,
    title: "Board Prep (Class 12)",
    subtitle: "Conquer Boards. Secure Your Future.",
    description:
      "Focused board preparation to master the Class 12 syllabus, maximize scores, and build college-ready confidence.",
    points: [
      "⚛️ Comprehensive Syllabus Coverage",
      "🧮 Advanced Problem Solving",
      "📖 Exam Simulation",
      "✨ Score Enhancement Strategy",
      "🎯 Targeted Doubt Clinics",
    ],
    image: "../images/thumbnails/batch thumbnail class 12.png",

    category: "Competitive",
    duration: "3-4 months",
    batchSize: "Small Batch",
    link: "/courses/class-12-board-preparation",
  },
];
export default function CourseOverviewSection() {
    return (
        <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-12 px-4">
            <Helmet>
                <title>Coaching Courses for Class 8-12 in Ghazipur | SSS Achievers</title>
                <meta name="description" content="Explore SSS Achievers' coaching courses in Ghazipur – Foundation courses for Class 8, 9, 11 and Board Preparation for Class 10 & 12. CBSE, ICSE & UP Board. Enroll now!" />
                <link rel="canonical" href="https://www.sssachievers.com/courses" />
                <meta property="og:title" content="Coaching Courses for Class 8-12 in Ghazipur | SSS Achievers" />
                <meta property="og:description" content="Foundation and board prep courses for Class 8-12 at SSS Achievers, Ghazipur's best coaching institute." />
                <meta property="og:url" content="https://www.sssachievers.com/courses" />
            </Helmet>
            <div className="max-w-7xl mx-auto">

                <div className="text-center mb-16">
                    <div className="inline-flex items-center gap-2 bg-[#0097b2]/10 px-4 py-2 rounded-full mb-6">
                        <GraduationCap className="h-5 w-5 text-[#0097b2]" />
                        <span className="text-[#0097b2] font-semibold">SSS Achievers Courses</span>
                    </div>
                    <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-6">
                        Unlock Your Potential with
                        <span className="block text-[#0097b2] mt-2">Expert Coaching Programs</span>
                    </h1>
                    <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                        At SSS Achievers, we offer top-notch coaching for government exams, competitive tests, and academic success. Explore our courses designed to help you achieve your career aspirations and excel in your chosen field.
                    </p>
                </div>

                {/* Courses Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-10">
                    {courses.map((course) => (
                        <Card
                            key={course.id}
                            className="group hover:shadow-2xl transition-all duration-500 bg-white border-0 overflow-hidden rounded-2xl"
                        >
                            <div className="relative">
                                <img
                                    src={course.image || "/placeholder.svg"}
                                    alt={course.title}
                                    height={400}
                                    className="w-full object-cover"
                                />
                            </div>

                            <CardContent className="p-8">
                                {/* Course Header */}
                                <div className="mb-4">
                                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{course.title}</h3>
                                </div>

                                {/* Description */}
                                <p className="text-gray-700 mb-4 leading-relaxed text-sm">{course.description}</p>

                                {/* Key Highlights */}
                                <div className="mb-6">
                                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                        <GraduationCap className="h-4 w-4 text-[#0097b2]" />
                                        Key Highlights
                                    </h4>
                                    <div className="grid grid-cols-2 gap-2">
                                        {course.points.slice(0, 4).map((highlight, index) => (
                                            <div key={index} className="flex items-center gap-2 text-sm text-gray-600">
                                                <CheckCircle size={10} className="h-3 w-3 text-green-500" />
                                                <span className="truncate">{highlight}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Action Button */}
                               <Link to={course.link} onClick={() => window.scrollTo(0, 0)}>
                               
                                <Button
                                    style={{ border: '1px solid #0097b2', borderRadius: '4px', backgroundColor: '#0097b2', color: 'white', transition: 'ease-in-out' }}
                                    className="w-full bg-[#0097b2] hover:bg-[#007d91] text-white py-3 text-lg font-semibold rounded transition-all duration-300 transform hover:scale-105"
                                    size="lg"
                                >
                                    VIEW DETAILS
                                </Button></Link>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            </div>
        </div>
    )
}